/*
 * Hardcoded policy / legal content for ZENRADING SHOP.
 *
 * Content baseline was migrated from the MySQL `notice` table (ids 1-6) so the
 * About page no longer depends on the backend `/api/v1/notice` endpoint.
 *
 * Brand placeholders ({{shopName}} / {{company}} / {{phone}} / {{email}} /
 * {{address}}) have been substituted statically. Privacy Policy was
 * deduplicated, Refund TAT was changed to 3-5 working days, and Indian
 * compliance clauses were added per the project plan.
 */

export const SHOP_NAME = 'ZENRADING SHOP'
export const COMPANY = 'ZENRADING EXIM PRIVATE LIMITED'
export const PHONE = '+91 9875403111'
export const EMAIL = 'zenradingexim5@gmail.com'
export const ADDRESS = 'Office No. 209, Building No. 03, MJ Complex, Veer Savarkar Block, New Delhi, East Delhi, Delhi - 110092, India'

export const LAST_UPDATED = '05/09 2025'

const STORY_BODY = `# About ${SHOP_NAME}

**${SHOP_NAME}** is a fashion brand founded by **${COMPANY}** in 2025, born from a deep passion for blending traditional Indian attire with contemporary style. We believe every Indian woman deserves access to high-quality, fashionable clothing that celebrates cultural heritage while offering modern elegance — all at accessible prices.

## Our Philosophy

Our design inspiration draws from India's rich and diverse cultural legacy. We are committed to:

> **Redefining everyday fashion for Indian women** by seamlessly integrating traditional craftsmanship with modern tailoring techniques.

## Our Commitment

### Quality & Service
- 100% genuine quality assurance
- Exceptional customer service
- Environmentally conscious packaging
- Support for the "Skill India" initiative — training rural women in tailoring

### Social Responsibility
> **Empowering local artisan communities through sustainable fashion practices.**

---

## Why Choose ${SHOP_NAME}?

### Authentic Indian Style
- Curated by local designers from Delhi, Mumbai, and Bangalore

### Size Inclusive
- Comprehensive range from **XXS to XXL**
- Specifically tailored for Indian body types

### Hassle-Free Shopping
- **Free shipping** across India
- **7-day easy return** policy

### Artisan Collaboration
- Each garment tells a unique story of traditional Indian craftsmanship

---

## Contact Information

- **Phone:** ${PHONE}
- **Email:** ${EMAIL}
- **Address:** ${ADDRESS}

---

*Join us in celebrating the beauty of Indian tradition meets modern fashion!*
`

const PRIVACY_BODY = `# Privacy Policy

Thank you for being part of the **${SHOP_NAME}** community! At **${COMPANY}**, we pride ourselves on providing a trusted and stylish online shopping destination for women's fashion. Your privacy is incredibly important to us, and we are dedicated to protecting your personal information and ensuring you feel confident shopping with us.

Should you have any questions or concerns about this privacy notice or how your personal information is handled, please contact us at **${PHONE}** or **${EMAIL}**.

When you visit our website or use any of our services (collectively referred to as the **"Services"**, including our Website), we value the trust you place in us by sharing your personal information. This privacy notice is designed to clearly explain what information we collect, how we use it, and your rights regarding your data.

> **Please take time to review this notice carefully.** If you do not agree with any of the terms outlined, we recommend discontinuing the use of our Services immediately.

This Privacy Policy is published in compliance with the **Information Technology Act, 2000**, the **Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011**, and the **Digital Personal Data Protection Act, 2023 (DPDP Act)**.

---

## 1. What Information Do We Collect?

We collect personal information that you provide directly to us as well as information automatically gathered during your interaction with our Website. The type of information collected depends on how you engage with our Services.

### Personal Information You Provide
When creating an account, placing an order, or signing up for promotional updates, you may provide personal details such as your:

- Name
- Phone number
- Email address
- Shipping and billing addresses
- Account username and password
- Payment information (e.g., debit/credit card details, UPI handle)
- Contact preferences

### Information Automatically Collected
Certain data is automatically collected when you browse our Website, such as:

- Device and browser information
- IP address
- Operating system and language preferences
- Referring URLs
- Location information and usage patterns

This information helps us improve the Website's functionality, keep your shopping experience secure, and analyze trends.

---

## 2. How Do We Use Your Information?

The information we collect is used to enhance your shopping experience and support various business purposes. Specifically, we use your data to:

- Facilitate account creation and login
- Process and deliver your orders efficiently
- Send marketing and promotional updates about new arrivals, exclusive discounts, and seasonal collections (subject to your separate marketing consent — see Section 11)
- Share testimonials from satisfied customers
- Enforce our terms, conditions, and policies
- Improve the Website and tailor your shopping experience to your preferences
- Comply with applicable Indian laws and respond to lawful requests from authorities

---

## 3. User Consent

By creating an account, placing an order, subscribing to communications, or otherwise using our Services, you provide your **explicit consent** to the collection, storage, processing, and limited sharing of your personal data as described in this Privacy Policy.

- Consent is collected via clear opt-in mechanisms at the point of data collection (signup form, checkout, newsletter form).
- Sensitive personal data (e.g., payment instrument details) is collected only for the specific purpose of completing a transaction, with your express consent.
- You may **withdraw your consent at any time** by writing to our Grievance Officer (Section 13). Withdrawal of consent may prevent us from continuing to provide certain Services.

---

## 4. Will Your Information Be Shared With Anyone?

We only share your personal information in the following cases:

- With your explicit consent
- To comply with legal requirements, court orders, or government requests
- To provide services essential to your shopping experience (e.g., payment processors, logistics partners)
- To protect our rights, property, or safety, or that of our customers

> Your data will **never be sold** or shared with third parties for unrelated marketing purposes.

---

## 5. Cookies and Other Tracking Technologies

Yes, ${SHOP_NAME} uses **cookies and similar technologies** to collect data and enhance your browsing experience. Cookies help us personalize your experience, such as remembering items in your cart or recommending products tailored to your style preferences. You may disable cookies through your browser settings, though some features of the Site may not function correctly without them.

---

## 6. Third-Party Websites

Our Website may contain links to third-party websites or advertisements not affiliated with ${SHOP_NAME}. While we strive to work with reputable partners, we cannot guarantee the safety or privacy of information shared on external sites.

> We recommend reviewing the privacy policies of third-party platforms before engaging with them.

---

## 7. Data Retention

We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law (for example, tax, GST, accounting, or fraud-prevention obligations). For example, we may retain order history to assist with returns or customer service inquiries.

---

## 8. Information Security

To protect your personal information, we employ **industry-standard security measures**, including encryption in transit (HTTPS), secure servers, and access controls. This ensures your data is safeguarded against unauthorized access, disclosure, or misuse. No method of transmission over the internet is, however, 100% secure; we cannot guarantee absolute security.

---

## 9. Children's Privacy

**${SHOP_NAME} is intended for use by individuals aged 18 years or older.**

- We do **not** knowingly collect personal data from anyone under the age of 18.
- If a minor wishes to use our Services, this must be done strictly under the supervision of, and with **verifiable consent from**, a parent or legal guardian, who will be deemed to have read, understood, and accepted this Privacy Policy on the minor's behalf.
- If we discover that we have inadvertently collected personal data from a child without verifiable parental consent, we will delete such data promptly. Parents or guardians who become aware of such collection should contact our Grievance Officer (Section 13).

This clause is aligned with the requirements of the **Digital Personal Data Protection Act, 2023**.

---

## 10. Your Privacy Rights

You have the following rights regarding your personal data:

- **Right to access** the information we hold about you
- **Right to correction** of inaccurate or incomplete data
- **Right to restrict or object** to certain uses of your information
- **Right to data portability** (where technically feasible)
- **Right to grievance redressal** (Section 13)
- **Right to deletion** of your data, subject to legal retention obligations (Section 12)

To exercise any of these rights, please contact our Grievance Officer using the details in Section 13.

---

## 11. Consent for Marketing Communication

Marketing communication (including SMS, email, WhatsApp, and push notifications about offers, new arrivals, and promotional events) is sent **only with your separate, explicit opt-in consent**.

- A dedicated checkbox at signup or checkout is used to capture marketing consent — this consent is **not bundled** with your acceptance of these Terms or this Privacy Policy.
- You may **opt out of marketing at any time** by:
  - Clicking the **"Unsubscribe"** link in any marketing email
  - Replying **STOP** to any marketing SMS / WhatsApp message
  - Updating preferences in *My Account → Settings → Communication Preferences*
  - Emailing **${EMAIL}** with the subject line "Unsubscribe Marketing"
- Transactional communication (order confirmations, shipping updates, refund notices, security alerts, legal notices) is **not** subject to marketing opt-out and will continue to be sent so long as you have an active account or order.

---

## 12. Account Deletion Procedure

You may request deletion of your ${SHOP_NAME} account and the associated personal data at any time.

**How to request deletion:**

1. Log in and navigate to **My Account → Settings → Delete Account**, or
2. Email **${EMAIL}** from your registered email address with the subject line "Account Deletion Request", or
3. Contact our Grievance Officer (Section 13).

**What happens after a deletion request:**

- We will acknowledge your request within **48 hours**.
- Your account will be **deactivated immediately** so it cannot be used to place new orders.
- Personal data will be **permanently erased within 30 days**, except for information we are required to retain under applicable Indian law (e.g., invoices retained under tax / GST law for the statutory period, or records needed to defend legal claims).
- Once erasure is complete, a confirmation notice will be sent to your registered email.

---

## 13. Grievance Officer

In accordance with the **Information Technology Act, 2000** and the **Consumer Protection (E-Commerce) Rules, 2020**, the contact details of our Grievance Officer are:

\`\`\`
Grievance Officer:  [Name to be filled]
Company:            ${COMPANY}
Address:            ${ADDRESS}
Email:              ${EMAIL}
Phone:              ${PHONE}
Working Hours:      Monday to Saturday, 10:00 - 18:00 IST
\`\`\`

The Grievance Officer will acknowledge any complaint within **48 hours** and will endeavour to redress it within **one (1) month** of receipt.

---

## 14. Do-Not-Track (DNT)

Many web browsers and mobile operating systems allow users to enable a **Do-Not-Track ("DNT")** setting, signaling a preference to limit tracking of online activities. Where applicable, we honor DNT signals.

---

## 15. Policy Updates

This Privacy Policy may be updated periodically to reflect changes to business practices, laws, or technology. The "Last Updated" date at the top of this page reflects the most recent revision. Material changes will be notified via email or a prominent banner on the Site, and the new version will take effect immediately upon posting.

---

Thank you for choosing **${SHOP_NAME} by ${COMPANY}** as your destination for chic, trendy fashion. Your privacy and satisfaction are our top priorities.
`

const SHIPPING_BODY = `# Shipping & Delivery Policy

Welcome to **${SHOP_NAME}**!

Thank you for choosing **${COMPANY}**. Our Shipping Policy is designed to ensure a seamless and transparent shopping experience. Please review the details below.

---

## 1. Order Processing Times

- All orders are processed within **2-3 business days**.
- Orders are not processed or shipped on Sundays or public holidays.

> **Please Note:** During peak seasons or sales events, processing times may be slightly extended. We appreciate your patience and will notify you promptly via email or phone if any significant delays occur.

---

## 2. Shipping Options & Delivery Timelines

Shipping costs are automatically calculated and displayed at checkout.

| Shipping Method | Estimated Delivery Time |
| :--- | :--- |
| Standard Shipping | 3 - 5 business days |
| Expedited Shipping | 1 - 3 business days |

*Please note that these are estimated timeframes; occasional delays due to carrier or unforeseen circumstances may occur.*

---

## 3. Shipment Confirmation & Order Tracking

- Once your order ships, you will receive a **shipment confirmation email / SMS** containing your tracking number.
- Tracking information usually becomes active on the carrier's website within **24 hours** of shipment.

---

## 4. International Shipping

We are delighted to offer **international shipping**. Shipping costs and delivery times vary by destination and will be shown during checkout.

---

## 5. Customs, Duties, and Taxes

> **Important:** For orders shipped outside of India, the recipient is solely responsible for any applicable **customs duties, import taxes, or fees**.
>
> ${SHOP_NAME} has no control over these charges and cannot predict their amount. Please contact your local customs office for more information.

---

## 6. Damages or Loss During Transit

If your order arrives damaged or appears tampered with at delivery:

- **Record an unboxing video** while opening the parcel.
- **Contact us within 24 hours** at ${EMAIL} or ${PHONE} with photos / video evidence.
- We will coordinate with the courier and arrange a replacement or refund as per our [Refund Policy](/about?id=4).

---

## 7. Returns & Refunds

Not satisfied with your purchase? Please refer to our dedicated [Refund Policy](/about?id=4) for detailed instructions on how to initiate a return or refund.

---

Thank you for trusting **${SHOP_NAME} by ${COMPANY}**. We're thrilled to be a part of your style journey!
`

const RETURNS_BODY = `# Refund & Return Policy

Thank you for shopping at **${SHOP_NAME}**! We hope you love your purchase. However, if you are not completely satisfied, you can return eligible items for a **full refund, store credit, or an exchange**. Please review the policy details below.

---

## Return Eligibility

- **Return Window:** Returns must be initiated within **7 days** of the purchase / delivery date.
- **Condition:** Items must be in **brand-new, unused condition** with all original tags, labels, and packaging intact.
- **Requirements:** Items must be free of any wear, stains, odors, or damage.

> **Important Exceptions (Non-Returnable Items):**
> - **Sale items** are **FINAL SALE**.
> - **Intimate apparel** (e.g., lingerie), socks, stockings, dupatta / scarves, and clearance items cannot be returned due to hygiene and promotional restrictions.

---

## How to Return an Item

### Step 1: Request a Return Authorization
Email our customer service at **${EMAIL}** to request a **Return Merchandise Authorization (RMA) number**. Please include your order number and reason for return.

### Step 2: Pack and Ship Your Return
- Securely package the item in its original packaging.
- Include your **proof of purchase** (order invoice / email).
- Clearly write your **RMA number** on the outside of the package.
- Ship your return to the address shared with your RMA confirmation email.

> **Return Shipping:** The customer is responsible for return shipping costs except where the product was wrong / damaged on arrival. We highly recommend using a **trackable shipping service** for your protection.

---

## Refunds & Processing

- **Inspection:** Upon receipt, we will inspect the returned item and confirm eligibility.
- **Refund TAT:** Once your return is approved, refunds, store credit, or exchanges are processed within **3 - 5 working days**.
- **Refund timeline to your account:** Refunds typically reflect on your original payment method (UPI / Bank / Card / Wallet) within **3 - 5 working days** of processing, depending on your bank. COD orders are refunded via bank transfer or wallet credit within the same window.
- **Notification:** You will receive an **email / SMS confirmation** once your return has been finalized.

---

## Damaged or Defective Items

If your item arrives **damaged or defective**, please contact us within **24 hours of delivery** with photos / unboxing video to arrange a refund or exchange.

**Contact Customer Support:**

- **Email:** ${EMAIL}
- **Phone:** ${PHONE}

---

## Questions?

If you have any questions about our return policy, please don't hesitate to contact us. Thank you for choosing **${SHOP_NAME}** — where every woman deserves to feel fabulous!
`

const CANCEL_BODY = `# Order Cancellation Policy

Thank you for shopping with **${SHOP_NAME}**, operated by **${COMPANY}**. We strive to provide a seamless shopping experience. If you need to cancel an order, please review the following policy.

---

## Cancellation Timeframes & Refund Details

| Timeframe | Cancellation Status | Refund Details |
| :--- | :--- | :--- |
| Within 24 hours of order placement | Full Cancellation Available | Full refund issued to original payment method |
| After 24 hours, but before shipment | Partial Cancellation Available | 10% handling fee deducted; remaining balance refunded |
| After order has been shipped | Cannot be cancelled | You may return the item upon delivery per our [Refund Policy](/about?id=4) |

All approved cancellation refunds are processed within **3 - 5 working days**.

---

## Non-Cancellable Orders

The following types of orders **cannot be cancelled** once placed:

- **Custom-made items** (e.g., personalised designs, made-to-measure pieces)
- **Pre-ordered items** from exclusive collections

These products are specially crafted to your specifications upon ordering.

---

## Exception Handling

If a cancellation is necessary due to an **error on our part** (e.g., wrong item shipped, item out of stock, prolonged delivery delay), we will process a **full refund with no deductions**.

---

## How to Request a Cancellation

To request an order cancellation, please contact our customer service team as soon as possible:

- **Email:** ${EMAIL}
- **Phone:** ${PHONE}

Please have your **order number** ready for faster assistance.

---

## Important Notes

- **Policy Updates:** ${COMPANY} reserves the right to update this policy under specific circumstances.
- **Return Policy:** For orders that have already shipped, please refer to our [Refund Policy](/about?id=4) for return instructions.

We appreciate your understanding. Thank you for choosing **${SHOP_NAME}**!
`

const TERMS_BODY = `# Terms of Service

Welcome to **${SHOP_NAME}**, operated by **${COMPANY}** ("we", "us", or "our"). These Terms of Service ("Terms") govern your access to and use of the ${SHOP_NAME} website (the "Site") and any services provided therein.

By accessing or using our Site, you agree to be bound by these Terms. If you do not agree to all of these terms and conditions, you must not access the Site or use our services.

---

## 1. Intellectual Property & License

### 1.1 Ownership
All content on this Site, including but not limited to text, images, logos, graphics, and designs, is the property of ${SHOP_NAME} or its licensors and is protected by intellectual property laws. All rights are reserved.

### 1.2 Limited License
We grant you a limited, non-exclusive, non-transferable, and revocable license to access and use the Site for personal, non-commercial purposes in accordance with these Terms.

### 1.3 Restrictions
You are expressly prohibited from:

- Republishing, selling, renting, or sub-licensing any material from the Site.
- Reproducing, duplicating, or copying content for commercial purposes.
- Redistributing content from ${SHOP_NAME} without prior written consent.

---

## 2. User Accounts

### 2.1 Account Responsibility
When you create an account, you are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.

### 2.2 Security
You must notify us immediately of any unauthorized use of your account or any other security breach. We reserve the right to disable any user account at our sole discretion.

---

## 3. Pricing & Payment

- All prices on the Site are listed in **Indian Rupees (INR / ₹)** and are **inclusive of GST** unless stated otherwise.
- Accepted payment methods include **Cash on Delivery (COD)**. **UPI / online payment is currently being upgraded and will be re-enabled soon.**
- We reserve the right to **correct pricing or product description errors** at any time, including after an order has been placed. In such cases, you will be notified and offered the option to cancel for a full refund.
- Prices, offers, and discounts are subject to change without prior notice.

---

## 4. Purchases, Cancellation & Refunds

All purchases made through the Site are subject to:

- Our [Refund Policy](/about?id=4) — full eligibility, processing windows, and a **3 - 5 working day** refund TAT.
- Our [Order Cancellation Policy](/about?id=5) — cancellation windows and applicable handling fees.
- Final-sale and personalised items may be non-refundable as listed in the Refund Policy.

---

## 5. Shipping & Delivery Terms

Shipping rates, processing times, courier partners, customs handling, and lost-or-damaged-in-transit handling are governed by our [Shipping & Delivery Policy](/about?id=3), which is incorporated into these Terms by reference. Estimated delivery timelines are best-effort and not guaranteed.

---

## 6. Linking to Our Site

### 6.1 Approved Links
The following organizations may link to our Site without prior written approval:

- Government agencies
- Search engines
- News organizations
- Online directory distributors

### 6.2 Link Approval
Other organizations, including fashion influencers and blogs, may request link approval by contacting us. We welcome links that align with our brand values.

---

## 7. Prohibited Uses

You may not:

- Use iframes or similar techniques to alter the visual presentation of our Site without written consent.
- Link to our Site from any website that is defamatory, obscene, illegal, or infringes on any third-party rights.
- Use automated tools (scrapers, bots) to access or harvest data from the Site.
- Attempt to interfere with the security or integrity of the Site.

---

## 8. Force Majeure

Neither party shall be liable for any delay or failure in performance of any obligations under these Terms (other than payment obligations) where such delay or failure arises from circumstances beyond its reasonable control, including but not limited to acts of God, natural disasters, fire, flood, earthquake, pandemic / epidemic, war, terrorism, civil unrest, government actions, labour strikes, internet / telecommunication outages, or courier failures. Affected obligations will be suspended for the duration of the force majeure event.

---

## 9. Termination & Suspension

We reserve the right, at our sole discretion and without prior notice, to **suspend, restrict, or terminate** your access to the Site and / or your account if we have reasonable grounds to believe that:

- You have breached these Terms or any applicable law.
- You have engaged in fraudulent activity, payment chargebacks, abuse of the return / refund process, or other behaviour harmful to ${SHOP_NAME} or its customers.
- Continued provision of the Service to you is prohibited by law or by court order.
- You have failed to pay any amount due.

Upon termination, your right to use the Site ends immediately, but provisions of these Terms that by their nature should survive (e.g., Intellectual Property, Indemnification, Governing Law) will continue to apply.

---

## 10. Compliance with Indian E-Commerce Laws

${COMPANY} operates ${SHOP_NAME} in compliance with applicable Indian laws, including without limitation:

- The **Consumer Protection Act, 2019** and the **Consumer Protection (E-Commerce) Rules, 2020**.
- The **Information Technology Act, 2000** and the rules made thereunder, including the **Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021**.
- The **Digital Personal Data Protection Act, 2023 (DPDP Act)**.
- The **Central Goods and Services Tax Act, 2017** and applicable State GST Acts.
- The **Legal Metrology Act, 2009** and the **Legal Metrology (Packaged Commodities) Rules, 2011**.

You agree to use the Site in compliance with all applicable Indian laws.

---

## 11. Indemnification

You agree to **indemnify, defend, and hold harmless** ${COMPANY}, its directors, officers, employees, and affiliates from and against any and all claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in any way connected with:

- Your breach of these Terms or any applicable law.
- Your misuse of the Site or its content.
- Your infringement of any third-party right (intellectual property, privacy, or otherwise).
- Any content you submit, post, or transmit through the Site.

---

## 12. Electronic Communication Consent

By using the Site or providing your contact details, you **consent to receive communications from us in electronic form** (email, SMS, WhatsApp, push notifications, or in-app messages) and you agree that all agreements, notices, disclosures, invoices, order confirmations, and other communications that we provide to you electronically **satisfy any legal requirement that such communications be in writing**.

You may opt out of marketing communications as described in the [Privacy Policy](/about?id=2), Section 11. You cannot opt out of transactional or legally required communications while you maintain an active account or order.

---

## 13. Privacy

Your privacy is important to us. Please read our [Privacy Policy](/about?id=2) to understand how we collect, use, and protect your personal information.

---

## 14. Reservation of Rights

We reserve the right to:

- Request the removal of any link to our Site.
- Modify these Terms at any time. Continued use of the Site after changes constitutes acceptance of the revised Terms.

---

## 15. Disclaimer

To the fullest extent permitted by law, ${SHOP_NAME} provides the Site and its content on an **"as is"** basis. We make no warranties regarding the accuracy or completeness of any content. We shall not be liable for any loss or damage arising from your use of the Site or any information contained therein, except as required by applicable law.

---

## 16. Severability

If any provision of these Terms is held by a court of competent jurisdiction to be invalid, illegal, or unenforceable for any reason, such provision shall be deemed severed from these Terms and the remaining provisions shall continue in full force and effect. The invalid provision shall, where possible, be replaced by a valid provision that most closely reflects the original intent of the parties.

---

## 17. Governing Law & Jurisdiction

These Terms shall be governed by and construed in accordance with the **laws of India**, without regard to its conflict of law principles. The courts at **New Delhi, Delhi** shall have **exclusive jurisdiction** over any dispute, claim, or controversy arising out of or in connection with these Terms or your use of the Site, subject to the requirements of the Consumer Protection Act, 2019, where applicable.

---

## 18. Contact Us

If you have any questions about these Terms, please contact us at:

- **Company:** ${COMPANY}
- **Email:** ${EMAIL}
- **Phone:** ${PHONE}
- **Address:** ${ADDRESS}

Thank you for choosing ${SHOP_NAME}!
`

export const policies = {
  1: { key: 'story',    title: 'Our Story',           lastUpdated: LAST_UPDATED, body: STORY_BODY },
  2: { key: 'privacy',  title: 'Privacy Policy',      lastUpdated: LAST_UPDATED, body: PRIVACY_BODY },
  3: { key: 'shipping', title: 'Shipping & Delivery', lastUpdated: LAST_UPDATED, body: SHIPPING_BODY },
  4: { key: 'returns',  title: 'Refund & Return Policy', lastUpdated: LAST_UPDATED, body: RETURNS_BODY },
  5: { key: 'cancel',   title: 'Order Cancellation',  lastUpdated: LAST_UPDATED, body: CANCEL_BODY },
  6: { key: 'terms',    title: 'Terms of Service',    lastUpdated: LAST_UPDATED, body: TERMS_BODY }
}

export default policies
